import { Toggle as TogglePrimitive } from '@base-ui/react/toggle';
import { cva, type VariantProps } from 'class-variance-authority';

import { toggleVariants } from '@/components/buttons/Toggle/Toggle';
import { cn } from '@/lib/utils';

import { useToggleGroupContext } from './ToggleGroupContext';

export type ToggleGroupItemProps = TogglePrimitive.Props & VariantProps<typeof toggleVariants>;

const JOINED_OUTER_RADIUS = {
  small:
    'group-data-horizontal/toggle-group:data-[spacing=0]:first:rounded-l-sm group-data-horizontal/toggle-group:data-[spacing=0]:last:rounded-r-sm group-data-vertical/toggle-group:data-[spacing=0]:first:rounded-t-sm group-data-vertical/toggle-group:data-[spacing=0]:last:rounded-b-sm',
  medium:
    'group-data-horizontal/toggle-group:data-[spacing=0]:first:rounded-l-md group-data-horizontal/toggle-group:data-[spacing=0]:last:rounded-r-md group-data-vertical/toggle-group:data-[spacing=0]:first:rounded-t-md group-data-vertical/toggle-group:data-[spacing=0]:last:rounded-b-md',
} as const;

const toggleGroupItemVariants = cva(['shrink-0 px-2 focus:z-10 focus-visible:z-10'], {
  variants: {
    variant: {
      secondary: [
        'border border-gray-900',
        'group-data-[spacing=0]/toggle-group:rounded-none',

        'group-data-horizontal/toggle-group:data-[spacing=0]:border-l-0 group-data-horizontal/toggle-group:data-[spacing=0]:first:border-l',

        'group-data-vertical/toggle-group:data-[spacing=0]:border-t-0 group-data-vertical/toggle-group:data-[spacing=0]:first:border-t',
      ],
      ghost: ['group-data-[spacing=0]/toggle-group:rounded-none'],
    },
  },
});

const ToggleGroupItem = ({
  className,
  children,
  variant,
  size,
  shape = 'square',
  ...props
}: ToggleGroupItemProps) => {
  const context = useToggleGroupContext();
  const resolvedVariant = variant ?? context.variant;
  const resolvedSize = size ?? context.size;
  const isJoined = context.spacing === 0;
  const isSmallSize = resolvedSize === 'small' || resolvedSize === 'icon-small';

  return (
    <TogglePrimitive
      data-slot="toggle-group-item"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
      data-spacing={context.spacing}
      className={cn(
        toggleGroupItemVariants({
          variant: resolvedVariant,
        }),
        isJoined && JOINED_OUTER_RADIUS[isSmallSize ? 'small' : 'medium'],
        toggleVariants({
          variant: resolvedVariant,
          size: resolvedSize,
          shape: isJoined ? 'square' : shape,
        }),
        className,
      )}
      {...props}
    >
      {children}
    </TogglePrimitive>
  );
};

export default ToggleGroupItem;
